import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import { SubMenuService } from '../../../../services/submenu.service';
import { TourAnchorDirective } from '../../../../features/learning-center/engine/tour-anchor.directive';
import { MapLayerKey, MapLayerSelectionService } from '../../../../services/map-layer-selection.service';
import { CatchHeatService } from '../../../../services/catch-heat.service';
import { FIELD_TILES, HEAT_TILE, MapLayerTile, PIN_TILES } from '../../../../shared/utils/map-layer-tiles';

/**
 * The map's single layer picker — the merge of the former Overlays (ocean fields) and Layers
 * (marker overlays) sheets plus catch heat, as in Flutter's `MapLayerRail`.
 *
 * One entry is live at a time across every group and a tap applies it immediately (no staging, no
 * Apply step). Tapping the live entry again turns it off. View only: the selection rules live in
 * `MapLayerSelectionService`.
 */
@Component({
  selector: 'app-map-layers',
  standalone: true,
  imports: [CommonModule, TourAnchorDirective],
  templateUrl: './map-layers.component.html',
  styleUrl: './map-layers.component.scss',
})
export class MapLayersComponent implements OnInit, OnDestroy {
  readonly heat = HEAT_TILE;
  readonly fields = FIELD_TILES;
  readonly pins = PIN_TILES;

  isOpen = false;
  isHeatMember = false;
  selected: MapLayerKey | null = null;

  private subs: Subscription[] = [];

  constructor(
    private submenu: SubMenuService,
    private selection: MapLayerSelectionService,
    private catchHeat: CatchHeatService,
  ) {}

  ngOnInit(): void {
    this.catchHeat.ensureMembership();
    this.subs.push(
      this.submenu.layerModalState$.subscribe((open) => (this.isOpen = open)),
      this.selection.selected$.subscribe((key) => (this.selected = key)),
      this.catchHeat.isMember$.subscribe((m) => (this.isHeatMember = m)),
      // A caller (e.g. the overlay widget's icon) asked to land on a specific field.
      this.submenu.overlaySelection$.subscribe((key) => {
        const tile = this.fields.find((t) => t.key === key);
        if (tile && this.selected !== tile.key) this.selection.select(tile.key);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  /** Spotlight target for the Layers tour: SST is the one representative tile. */
  tileAnchor(tile: MapLayerTile): string | null {
    return tile.key === 'sst' ? 'filters.layersFirstTile' : null;
  }

  close(): void {
    this.submenu.setLayerModalState(false);
  }

  /** Apply the tap and get the sheet out of the way so the map (and its player card) is visible. */
  onTileTap(tile: MapLayerTile): void {
    if (this.selected === tile.key) {
      this.selection.clear();
    } else {
      this.selection.select(tile.key);
      this.submenu.requestCollapseSidebar();
    }
    this.close();
  }
}
